import { Avatar, Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useProcurementPurchaseOrdersDatatable,
} from 'generated/resources/datatable.graphql'
import { compact } from 'lodash'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { PurchaseOrderStatusLabel } from 'modules/procurement/components/purchaseOrders/PurchaseOrderStatusLabel'

type ProcurementPurchaseOrdersDatatableProjections = Parameters<
  typeof useProcurementPurchaseOrdersDatatable
>[0][number]

const PoNumberProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'poNumber',
    title: i18n.t('procurement.purchase_orders.list.po_number'),
    kind: ProjectionKind.Custom,
    template: '{{ formattedPoNumber }}',
    render: ({ formattedPoNumber }) => {
      return (
        <Box minWidth="s64">
          <Text.Normal size="200" weight="medium">
            {formattedPoNumber}
          </Text.Normal>
        </Box>
      )
    },
    sortBy: 'poNumber',
  }
}

const DescriptionProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'description',
    title: i18n.t('procurement.purchase_orders.list.description'),
    kind: ProjectionKind.Custom,
    template: '{{ description }}',
    render: ({ description }) => {
      return (
        <Box minWidth="s120">
          <Text.Normal size="200" weight="medium">
            {description}
          </Text.Normal>
        </Box>
      )
    },
    sortBy: 'description',
    hidden: true,
  }
}

const VendorProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'vendor',
    title: i18n.t('procurement.purchase_orders.list.vendor'),
    kind: ProjectionKind.Custom,
    template: '{{ vendor.legalName }}',
    render: ({ vendor }) => {
      return <Text.Normal size="200">{contactFullName(vendor)}</Text.Normal>
    },
  }
}
const RequesterAccessProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'requesterAccess',
    title: i18n.t('procurement.purchase_orders.list.requested_by'),
    kind: ProjectionKind.Custom,
    template: '{{ purchaseRequest.requesterEmployee.fullName }}',
    render: ({ purchaseRequest }) => {
      return (
        <Box flexDirection="row" alignItems="center" gap="s16" minWidth="s96">
          <Avatar
            size="s40"
            url={buildUrl(purchaseRequest?.requesterEmployee?.avatar?.url)}
            label={purchaseRequest?.requesterEmployee?.fullName || ''}
          />
          <Text.Normal size="200" weight="medium">
            {purchaseRequest?.requesterEmployee?.fullName || ''}
          </Text.Normal>
        </Box>
      )
    },
  }
}
const StatusProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'status',
    title: i18n.t('procurement.purchase_orders.list.status'),
    kind: ProjectionKind.Custom,
    template: '{{ status }}',
    render: ({ status }) => {
      return <PurchaseOrderStatusLabel status={status} />
    },
    sortBy: 'status',
  }
}
const CostAmountProjection = (locale: string): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'costAmount',
    kind: ProjectionKind.Custom,
    title: i18n.t('procurement.purchase_orders.list.cost'),
    template: '{{ cost.cents | divided_by: 100.0 }}',
    textAlign: 'right',
    justifyContent: 'flexEnd',
    render: ({ cost }) => {
      return (
        <Box alignItems="flexEnd">
          {cost && (
            <Text.Normal size="200" weight="medium">
              {MoneyWithCurrency({
                amount: Number(cost.cents),
                currency: cost.currency,
                locale,
              })}
            </Text.Normal>
          )}
        </Box>
      )
    },
  }
}
const CostCurrencyProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'costCurrency',
    kind: ProjectionKind.Custom,
    title: '',
    template: '{{ cost.currency }}',
    render: () => <></>,
  }
}
const DateProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'date',
    title: i18n.t('procurement.purchase_orders.list.requested'),
    kind: ProjectionKind.Custom,
    template: '{{ date }}',
    render: ({ date }) => {
      return <Text.Normal size="200">{dateBackendDateTimeToUI(date)}</Text.Normal>
    },
    sortBy: 'date',
    colSpan: 1,
    hidden: true,
  }
}

const LegalEntityProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'legalEntity',
    title: i18n.t('procurement.purchase_orders.list.legal_entity'),
    kind: ProjectionKind.Custom,
    template: '{{ legalEntity.legalName }}',
    render: ({ legalEntity }) => {
      return <Text.Normal size="200">{legalEntity?.legalName}</Text.Normal>
    },
    hidden: true,
  }
}

const PurchaseRequestTypeProjection = (): ProcurementPurchaseOrdersDatatableProjections => {
  return {
    identifier: 'purchaseRequestType',
    title: i18n.t('procurement.purchase_orders.list.type'),
    kind: ProjectionKind.Custom,
    template: '{{ purchaseRequest.type.name }}',
    render: ({ purchaseRequest }) => {
      return <Text.Normal size="200">{purchaseRequest?.type?.name}</Text.Normal>
    },
  }
}

const DetailProjection = (
  handleDownloadPdf: (id: number) => void
): ProcurementPurchaseOrdersDatatableProjections => {
  const { navigation } = useNavigation()
  return {
    identifier: 'detail',
    kind: ProjectionKind.Decorative,
    render: ({ id, status }) => (
      <Box gap="s8" flexDirection="row">
        <PopoverMenu type="list">
          <PopoverMenu.Trigger aria-label="open purchase order actions">
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            <PopoverMenu.TouchableOption
              title={i18n.t('procurement.purchase_orders.list.download_pdf')}
              onClick={() => handleDownloadPdf(id)}
              disabled={status == 'draft'}
            />
          </PopoverMenu.Content>
        </PopoverMenu>
        <Button.Router
          aria-label="open purchase order details"
          type="secondary"
          to={navigation.procurement.purchaseOrders.detail.fullPath({ id })}
          iconName="arrow-right"
          onClick={() => track(trackingEvents.procurement.viewPurchaseOrderDetail)}
        />
      </Box>
    ),
    width: 120,
  }
}

export const PurchaseOrderProjections = ({
  locale,
  skipVendor,
  handleDownloadPdf,
}: {
  locale: string
  skipVendor: boolean
  handleDownloadPdf: (id: number) => void
}) =>
  compact([
    PoNumberProjection(),
    DescriptionProjection(),
    !skipVendor && VendorProjection(),
    RequesterAccessProjection(),
    PurchaseRequestTypeProjection(),
    StatusProjection(),
    CostAmountProjection(locale),
    CostCurrencyProjection(),
    DateProjection(),
    LegalEntityProjection(),
    DetailProjection(handleDownloadPdf),
  ])
